import { Close } from "@mui/icons-material";
import {
  Autocomplete,
  FormControl,
  IconButton,
  MenuItem,
  Modal,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { FormEvent, SyntheticEvent, useCallback, useState } from "react";
import { useGetProductsQuery } from "src/api/services/productsApi";
import { useCreatePromoBannerMutation } from "src/api/services/bannersApi";
import { Languages, Product } from "src/api/types";
import LoadingButton from "src/components/Buttons/LoadingButton/LoadingButton";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { desktopSx } from "src/utils/styles/modalStyles";
import { useSentryLogger } from "src/hooks/useSentryLogger";

type ModalTypes = {
  open: boolean;
  onClose: (o: boolean) => void;
};
const NewBannerModal = ({ open, onClose }: ModalTypes) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [bannerText, setBannerText] = useState<string>("");
  const [product, setProduct] = useState<Product | null>(null);
  const [language, setLanguage] = useState<Languages | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { products } = useGetProductsQuery(undefined, {
    selectFromResult: ({ data }) => {
      return { products: data?.data ?? [] };
    },
  });
  const [createBanner] = useCreatePromoBannerMutation();
  const clinicianName = useClinician(
    (clinician) => `${clinician.first_name} ${clinician.last_name}`,
  );
  const errLog = useSentryLogger("error");
  const languages: { value: Languages; title: string }[] = [
    { value: "en", title: "English" },
    { value: "es", title: "Spanish" },
    { value: "pt", title: "Portuguese" },
  ];

  const handleProduct = useCallback(
    (_: SyntheticEvent<Element | Event>, value: Product | null) => {
      setProduct(value);
    },
    [],
  );

  const handleNewBanner = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setLoading(true);
      if (!product || !bannerText || !language) {
        setError("Please fill out all fields");
        setLoading(false);
        return;
      }
      try {
        await createBanner({
          text: bannerText,
          product_id: product.id,
          product_category_id: product.category.id,
          language: language,
          created_by: clinicianName,
        }).unwrap();

        onClose(false);
        setBannerText("");
        setLanguage(null);
        setProduct(null);
      } catch (error) {
        errLog("Error creating new banner", error);
      } finally {
        setLoading(false);
      }
    },
    [
      bannerText,
      clinicianName,
      createBanner,
      errLog,
      language,
      onClose,
      product,
    ],
  );

  return (
    <Modal open={open}>
      <Stack sx={desktopSx} gap={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography fontWeight={700} variant="h5">
            New flow banner
          </Typography>
          <IconButton onClick={() => onClose(false)}>
            <Close fontSize="medium" />
          </IconButton>
        </Stack>
        <Stack
          gap={2}
          component="form"
          noValidate
          autoComplete="off"
          onSubmit={handleNewBanner}
        >
          <FormControl>
            <TextField
              label="Banner text"
              value={bannerText}
              onChange={(e) => setBannerText(e.target.value)}
              required
            />
          </FormControl>
          <FormControl>
            <Autocomplete
              options={products}
              getOptionLabel={(p) => p.name}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField {...params} label="Product" required />
              )}
              value={product}
              onChange={handleProduct}
            />
          </FormControl>
          <FormControl>
            <TextField
              select
              required
              label="Language"
              value={language}
              onChange={(e) => setLanguage(e.target.value as Languages)}
            >
              {languages.map((lang) => (
                <MenuItem value={lang.value} key={lang.value}>
                  {lang.title}
                </MenuItem>
              ))}
            </TextField>
          </FormControl>
          {error ? (
            <Typography sx={{ color: "red" }} textAlign="center">
              {error.toString()}
            </Typography>
          ) : null}
          <LoadingButton loading={loading} type="submit">
            Create
          </LoadingButton>
        </Stack>
      </Stack>
    </Modal>
  );
};

export default NewBannerModal;
